import React from 'react';
import {
  freeTicketPeriodLabel,
  freeTicketGrantCaption,
  freeTicketRemainingCaption,
} from '../utils/freeTicketPeriod';

function TicketSkeleton({ className = '' }) {
  return (
    <span
      className={`inline-block h-6 w-8 rounded bg-slate-200 dark:bg-slate-600 animate-pulse align-middle ${className}`}
      aria-hidden
    />
  );
}

function asCount(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Free / golden / total ticket summary cards. */
export function TicketBalanceCards({ user, balances, loading = false, compact = false }) {
  const pad = compact ? 'p-2.5' : 'p-3';
  const numClass = compact ? 'font-bold text-lg tabular-nums' : 'font-bold text-lg tabular-nums';
  const periodLabel = freeTicketPeriodLabel(balances?.periodDays);
  const grantCaption = freeTicketGrantCaption({
    dailyLimit: balances?.dailyLimit,
    periodDays: balances?.periodDays,
  });
  const remainingCaption = freeTicketRemainingCaption({
    platformTickets: balances?.platformTickets,
    dailyLimit: balances?.dailyLimit,
  });
  const nftBonus = asCount(balances?.nftNormalAvailable ?? balances?.nftBonusToday) || 0;
  const seededGolden = asCount(user?.goldenTickets);
  const seededNormal = asCount(user?.tickets);
  const normalValue = asCount(balances?.normalTickets) ?? seededNormal;
  const goldenValue = asCount(balances?.goldenTickets) ?? seededGolden;
  const totalValue =
    asCount(balances?.totalSpendable) ??
    (normalValue != null && goldenValue != null ? normalValue + goldenValue : null);

  const renderCount = (value) => {
    if (!user) return '—';
    if (loading && value == null) return <TicketSkeleton />;
    return value ?? 0;
  };

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-3 gap-2 ${compact ? 'text-sm' : ''}`}>
      <div className={`rounded-lg border border-slate-200 dark:border-slate-600 ${pad} bg-white dark:bg-gray-800`}>
        <div className="text-xs text-slate-500">{periodLabel}</div>
        <div className={numClass}>{renderCount(normalValue)}</div>
        {grantCaption ? (
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
            {grantCaption}
            {remainingCaption ? ` · ${remainingCaption}` : ''}
          </div>
        ) : null}
      </div>
      <div
        className={`rounded-lg border border-amber-200 dark:border-amber-800 ${pad} bg-amber-50/50 dark:bg-amber-950/20`}
      >
        <div className="text-xs text-slate-500">Golden</div>
        <div className={`${numClass} text-amber-800 dark:text-amber-200`}>
          {renderCount(goldenValue)}
        </div>
      </div>
      <div
        className={`rounded-lg border border-emerald-200 dark:border-emerald-800 ${pad} bg-emerald-50/50 dark:bg-emerald-950/20`}
      >
        <div className="text-xs text-slate-500">Total available</div>
        <div className={`${numClass} text-emerald-800 dark:text-emerald-200`}>
          {renderCount(totalValue)}
        </div>
        {!loading && nftBonus > 0 && (
          <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5">
            +{nftBonus} from NFT holdings
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketBalanceCards;
